import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import he from "he";
import { bpVars } from "../../functions/themeAssets";
import { STAGE_PATHS } from "../bigpicture/gameApi";
import { bannerSrc, Loader, StageHeader, SubmitButton } from "../bigpicture/components";
import { formatClock, useStage, useStopwatch } from "../bigpicture/useStage";
import "../styles/bp-fast.css";

const SUBSCRIBE_TEXT = "Subscribe to any PLAN to play with your peers.";

/** "... is TIMED. ..." — the word TIMED is highlighted. */
function Instruction({ text }) {
  const parts = String(text || "").split(/\b(TIMED)\b/i);
  return parts.map((part, i) =>
    /^timed$/i.test(part) ? (
      <strong key={i} className="tf-timed">
        {part}
      </strong>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
}

/** Challenge 1 — Think Fast: answer the listed tasks as fast as you can (think-fast.php). */
export default function ThinkFast() {
  const navigate = useNavigate();
  const { user, game, finish } = useStage(1);
  const [answers, setAnswers] = useState([]);
  const [saving, setSaving] = useState(false);
  const seconds = useStopwatch(game?.state?.elapsed, !!game);

  const questions = useMemo(() => (game?.content?.questions || []).map((q) => he.decode(String(q || ""))), [game]);

  if (!game) return <Loader />;
  const theme = game.theme;
  const isDemo = user.role === "demobypass" || String(user.sessionId || "").startsWith("demo");

  const setAnswer = (i, value) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[i] = value;
      return next;
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    // trailing / double spaces are cleaned up before sending
    const cleaned = questions.map((_, i) => String(answers[i] || "").trim().replace(/\s+/g, " "));
    if (cleaned.every((a) => a === "")) {
      Swal.fire("Please answer at least one question.", "", "warning");
      return;
    }

    setSaving(true);
    const result = await finish({ answers: cleaned });
    if (!result) {
      setSaving(false);
      return;
    }

    if (isDemo) {
      // demo players only get the first challenge, then are pointed to the plans
      await Swal.fire({ title: SUBSCRIBE_TEXT, icon: "success", timer: 1500 });
      window.location.href = `${process.env.REACT_APP_BASE_URL || ""}/plans`;
      return;
    }
    navigate(STAGE_PATHS[result.stage], { replace: true });
  };

  return (
    <div className="bp-fast" style={bpVars(theme)}>
      <StageHeader clock={formatClock(seconds)} points={game.state.score} src={bannerSrc(theme, 1)} alt="Challenge 1" />

      <div className="tf-wrapper">
        <div className="tf-card">
          <div className="tf-instructions">
            <div className="tf-instructions-text">
              <Instruction text={theme.landing_page_title1} />
            </div>
          </div>

          <form onSubmit={onSubmit}>
            {questions.map((question, i) => (
              <div className="tf-task-row" key={i}>
                <div className="tf-q-badge" style={{ backgroundColor: theme.landing_page_button_bgcolor, color: theme.landing_page_button_color }}>
                  {i + 1}
                </div>
                <div className="tf-q-text">{question}</div>
                <div className="tf-q-separator" aria-hidden="true" />
                <input
                  type="text"
                  className="tf-answer-input"
                  maxLength={200}
                  id={`ans${i + 1}`}
                  name={`ans${i + 1}`}
                  placeholder="Type your answer..."
                  value={answers[i] || ""}
                  onChange={(e) => setAnswer(i, e.target.value)}
                  required
                />
              </div>
            ))}

            <SubmitButton disabled={saving} />
          </form>
        </div>
      </div>
    </div>
  );
}

